// What an invitation link is, read before signing in (ADR-0046): `POST /v1/invitations/preview` with the
// token in the body — never in the address. While the invitation can still be accepted it answers who
// invited whom to what, until when, and the invited address or number in full; once it cannot (used,
// withdrawn, expired), its status and nothing else. The accept page and sign-up read it under one key, so
// the second reads what the first already did.

import type { InvitationPreview } from '@clavian/api-client';
import { useQuery } from '@tanstack/react-query';

import { useApi } from '../api/client';

/** One preview per token. The token is the invitation's secret: it stays in memory, under this key. */
export const invitationPreviewKey = (token: string) => ['invitation', 'preview', token] as const;

export function useInvitationPreview(token: string | undefined) {
  const api = useApi();
  return useQuery<InvitationPreview>({
    queryKey: invitationPreviewKey(token ?? ''),
    queryFn: () => api.previewInvitation({ body: { token: token ?? '' } }),
    enabled: token !== undefined,
    retry: false,
    staleTime: 60_000,
  });
}

/** An invitation that can still be accepted, with everything the preview says only then. */
export interface PendingInvitation {
  readonly status: 'pending';
  readonly organizationName: string;
  readonly role: string;
  readonly invitedBy: 'hq' | 'member';
  readonly inviterName: string | null;
  readonly channel: 'email' | 'phone';
  /** The number or address invited, masked. */
  readonly destination: string;
  /** The number or address invited, in full. */
  readonly contact: { readonly email?: string; readonly phoneNumber?: string };
  readonly expiresAt: string;
}

/**
 * The preview of an invitation that can still be accepted, or `undefined`: not read yet, or used, withdrawn
 * or expired — the preview of which is its status alone. Pure.
 */
export function pendingInvitationOf(preview: InvitationPreview | undefined): PendingInvitation | undefined {
  if (preview?.status !== 'pending') return undefined;
  const { organizationName, role, invitedBy, inviterName, channel, destination, contact, expiresAt } = preview;
  if (
    organizationName === undefined ||
    role === undefined ||
    invitedBy === undefined ||
    channel === undefined ||
    destination === undefined ||
    contact === undefined ||
    expiresAt === undefined
  ) {
    return undefined;
  }
  return { status: 'pending', organizationName, role, invitedBy, inviterName: inviterName ?? null, channel, destination, contact, expiresAt };
}
